import { useEffect, useMemo, useRef, useState } from "react";
import api, { fmtTime, apiError } from "@/lib/api";
import { toast } from "sonner";
import { format, startOfDay, endOfDay, addDays, isSameDay, startOfWeek } from "date-fns";
import { it } from "date-fns/locale";
import { MapPin, Phone, Check, CalendarClock, Plus, Loader2, Navigation, PanelLeft, X, Ban } from "lucide-react";
import { RichiestePanel } from "@/components/RichiestePanel";
import { AgendaTimeGrid } from "@/components/AgendaTimeGrid";
import { TIPO_META } from "@/lib/agendaTipi";
import { MiniCalendar } from "@/components/AgendaGCal";
import { useAuth } from "@/context/AuthContext";
import { EsitoModal, apptNeedsEsito } from "@/components/EsitoModal";
import { aggiornaConAnnulla } from "@/lib/agendaMove";

const COLL = { proprietario: "proprietari", acquirente: "acquirenti" };
const ini = (n) => (n || "").trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase() || "?";
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const kindOk = (sel) => (e) => {
  if (e.kind === "proposta_scadenza") return sel.includes("scadenza");
  if (e.kind === "visita") return sel.includes("acquirente");
  if (e.da_classificare || !e.tipo || !TIPO_META[e.tipo]) return true;
  return sel.includes(e.tipo);
};

function Drawer({ open, onClose, cur, onPick, onNew, onBlocca, kinds, selKinds, toggleKind }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[55] bg-black/30" onClick={onClose} data-testid="mob-drawer">
      <div onClick={(e) => e.stopPropagation()} className="absolute left-0 top-0 bottom-0 w-[86%] max-w-[320px] bg-white p-5 overflow-y-auto space-y-6 shadow-2xl pop-in">
        <div className="flex items-center justify-between"><span className="font-display font-extrabold text-xl text-[#2B2B2B]">Agenda</span>
          <button data-testid="mob-drawer-close" onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"><X size={18} /></button></div>
        <div className="space-y-2">
          <button data-testid="mob-drawer-crea" onClick={() => { onClose(); onNew(); }} className="inline-flex items-center gap-3 pl-3 pr-6 py-3 rounded-2xl bg-white border border-[var(--border)] shadow-[0_4px_14px_rgba(43,43,43,0.12)] font-bold text-[#2B2B2B]">
            <span className="w-8 h-8 rounded-full bg-[#FC82ED] flex items-center justify-center"><Plus size={20} /></span> Crea</button>
          {onBlocca && <button data-testid="mob-blocca" onClick={() => { onClose(); onBlocca(); }} className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold text-[#2B2B2B]"><Ban size={15} /> Blocca agenda</button>}
        </div>
        <MiniCalendar value={cur} onPick={(d) => { onPick(d); onClose(); }} />
        <div>
          <h4 className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#2B2B2B] mb-2">I miei calendari</h4>
          {kinds.map(([k, l, c]) => { const on = selKinds.includes(k); return (
            <button key={k} data-testid={`mob-kind-${k}`} onClick={() => toggleKind(k)} className="w-full flex items-center gap-3 py-2 text-left">
              <span className="w-[18px] h-[18px] rounded-md border-2 flex items-center justify-center" style={{ borderColor: c, background: on ? c : "transparent" }}>{on && <Check size={12} strokeWidth={3} className="text-white" />}</span>
              <span className={`text-sm ${on ? "text-[#2B2B2B] font-medium" : "text-slate-400"}`}>{l}</span></button>); })}
        </div>
      </div>
    </div>
  );
}

function DayStrip({ cur, setCur }) {
  const touch = useRef(null);
  const days = Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(cur, { weekStartsOn: 1 }), i));
  return (
    <div className="grid grid-cols-7 mb-3" data-testid="mob-day-strip"
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 50) setCur(addDays(cur, dx < 0 ? 7 : -7)); touch.current = null; }}>
      {days.map((d) => { const today = isSameDay(d, new Date()), sel = isSameDay(d, cur); return (
        <button key={d.toISOString()} data-testid={`mob-day-${format(d, "yyyy-MM-dd")}`} onClick={() => setCur(startOfDay(d))} className="flex flex-col items-center gap-1 py-1">
          <span className={`text-[10px] font-bold tracking-wider ${today ? "text-[#B8369F]" : "text-slate-500"}`}>{format(d, "EEE", { locale: it }).toUpperCase()}</span>
          <span className={`w-9 h-9 rounded-full flex items-center justify-center text-base font-extrabold transition-colors ${today ? "bg-[#FC82ED] text-[#2B2B2B]" : sel ? "bg-[#2B2B2B] text-white" : "text-[#2B2B2B]"}`}>{format(d, "d")}</span>
        </button>); })}
    </div>
  );
}

export function MobileAgenda({ onEdit, onNew, onCreateAt, onEventClick, reloadKey, kinds, selKinds, toggleKind, colorOf, onBlocca }) {
  const [tab, setTab] = useState("giorno");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [esitoAppt, setEsitoAppt] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  useEffect(() => { api.get("/agenda/circle-members", { params: { tutti: true } }).then((r) => setMembers(r.data || [])).catch(() => {}); }, []);
  const nameById = useMemo(() => {
    const m = {}; (members || []).forEach((a) => { if (a.id) m[a.id] = a.name; });
    if (user?.id) m[user.id] = user.name; return m;
  }, [members, user]);
  const canEditEvent = (e) => e.kind === "appuntamento" && e.tipo !== "bloccato" && !e.masked && e.can_edit !== false
    && (["direzione", "segreteria", "direttore_commerciale"].includes(user?.role) || e.owner_agent_id === user?.id || (e.partecipanti_ids || []).includes(user?.id));
  const [gcur, setGcur] = useState(startOfDay(new Date()));
  const [gevents, setGevents] = useState([]);
  const touch = useRef(null);
  const loadGrid = async () => {
    try { const { data } = await api.get("/agenda/events", { params: { start: startOfDay(gcur).toISOString(), end: endOfDay(gcur).toISOString() } }); setGevents(data || []); }
    catch { setGevents([]); }
  };
  useEffect(() => { if (tab === "giorno") loadGrid(); }, [tab, gcur, reloadKey]); // eslint-disable-line
  const gMove = (e, newISO) => aggiornaConAnnulla(e, { data: newISO }, loadGrid);
  const gResize = (e, newDur) => { if (newDur !== (e.durata_min || 60)) aggiornaConAnnulla(e, { durata_min: newDur }, loadGrid); };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agenda/events", { params: { start: startOfDay(gcur).toISOString(), end: endOfDay(addDays(gcur, 6)).toISOString() } });
      const appts = (data || []).filter((e) => e.kind === "appuntamento");
      appts.sort((a, b) => (a.data || "").localeCompare(b.data || ""));
      setEvents(appts);
    } catch { setEvents([]); } finally { setLoading(false); }
  };
  useEffect(() => { if (tab === "elenco") load(); }, [tab, gcur, reloadKey]); // eslint-disable-line

  const ok = kindOk(selKinds || []);
  const byDay = useMemo(() => {
    const m = {};
    events.filter(ok).forEach((e) => { const k = (e.data || "").slice(0, 10); (m[k] = m[k] || []).push(e); });
    return m;
  }, [events, selKinds]); // eslint-disable-line

  const call = async (e) => {
    if (!e.cliente_id || !COLL[e.cliente_tipo]) return toast.message("Nessun cliente collegato");
    try {
      const { data } = await api.get(`/${COLL[e.cliente_tipo]}/${e.cliente_id}`);
      const tel = data.telefono || data.cellulare;
      if (!tel) return toast.message("Numero non disponibile o non autorizzato");
      window.location.href = `tel:${tel}`;
    } catch { toast.error("Impossibile recuperare il contatto"); }
  };
  const maps = (e) => { const q = e.immobile_titolo || e.luogo; if (q) window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`, "_blank"); };
  const waze = (e) => { const q = e.immobile_titolo || e.luogo; if (q) window.open(`https://waze.com/ul?q=${encodeURIComponent(q)}&navigate=yes`, "_blank"); };

  const dayKeys = Object.keys(byDay).sort();

  return (
    <div className="fade-up pb-20" data-testid="mobile-agenda">
      <div className="flex items-center gap-2 mb-3">
        <button data-testid="mob-menu" onClick={() => setDrawer(true)} title="Calendari" className="p-2 -ml-2 rounded-full hover:bg-slate-100"><PanelLeft size={22} /></button>
        <h1 className="font-display font-extrabold text-2xl tracking-tight text-[#2B2B2B] flex-1" data-testid="mob-label">{cap(format(gcur, "MMMM yyyy", { locale: it }))}</h1>
        <button data-testid="mob-oggi" onClick={() => setGcur(startOfDay(new Date()))} className="px-4 py-1.5 rounded-full border border-[var(--border)] text-sm font-bold">Oggi</button>
        <button onClick={onNew} data-testid="mob-new-btn" title="Crea" className="w-9 h-9 rounded-full bg-[#FC82ED] text-[#2B2B2B] flex items-center justify-center shadow-[0_6px_16px_rgba(252,130,237,0.45)] active:scale-95 transition-transform"><Plus size={20} /></button>
      </div>
      <div className="flex bg-slate-100 rounded-full p-1 mb-3">
        {[["giorno", "Giorno"], ["elenco", "Elenco"], ["richieste", "Richieste"]].map(([k, l]) => (
          <button key={k} data-testid={`mob-tab-${k}`} onClick={() => setTab(k)}
                  className={`flex-1 px-2 py-1.5 rounded-full text-xs font-bold transition-colors ${tab === k ? "bg-[#2B2B2B] text-white" : "text-slate-600"}`}>{l}</button>
        ))}
      </div>
      {tab !== "richieste" && <DayStrip cur={gcur} setCur={setGcur} />}
      {tab === "richieste" && <RichiestePanel />}
      {tab === "giorno" && (
        <div data-testid="mob-grid"
          onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 70) setGcur((d) => addDays(d, dx < 0 ? 1 : -1)); touch.current = null; }}>
          <AgendaTimeGrid days={[gcur]} events={gevents.filter(ok)} colorOf={colorOf} nameById={nameById}
                          onEventClick={onEventClick} onCreate={onCreateAt} onMove={gMove} onResize={gResize} canEdit={canEditEvent}
                          scrollKey={format(gcur, "yyyy-MM-dd")} compact />
        </div>
      )}
      {tab === "elenco" && <>
      {loading && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#FC82ED]" /></div>}
      {!loading && dayKeys.length === 0 && <p className="text-center text-sm text-[var(--muted)] py-10">Nessun appuntamento in programma.</p>}
      {!loading && dayKeys.map((k) => (
        <div key={k} className="mb-5">
          <div className={`text-xs font-extrabold uppercase tracking-wider mb-2 ${isSameDay(new Date(k), new Date()) ? "text-[#B8369F]" : "text-slate-500"}`}>
            {format(new Date(k), "EEEE d MMMM", { locale: it })}
          </div>
          <div className="space-y-2.5">
            {byDay[k].map((e) => (
                <div key={e.id} data-testid={`mob-appt-${e.id}`} className="rounded-2xl bg-white border border-[var(--border)] shadow-[0_4px_14px_rgba(43,43,43,0.06)] p-4" style={{ borderLeft: `5px solid ${colorOf(e)}` }}>
                  <button className="w-full text-left" onClick={() => onEventClick(e)} data-testid={`mob-appt-open-${e.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-[#2B2B2B]">{fmtTime(e.data)}</span>
                      <span className="text-[11px] uppercase tracking-wide text-[var(--muted)]">{TIPO_META[e.tipo]?.label || (e.tipo || "").replace(/_/g, " ")}</span>
                    </div>
                    <div className="font-bold text-[#2B2B2B] truncate mt-0.5">{e.titolo}</div>
                    {e.immobile_nel_cestino && <span data-testid={`mob-cestino-${e.id}`} className="inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Immobile nel Cestino</span>}
                  </button>
                  {(e.partecipanti_nomi || []).length > 0 && (
                    <div className="flex items-center gap-1 mt-1.5" data-testid={`mob-parts-${e.id}`}>
                      {(e.partecipanti_nomi).slice(0, 4).map((n, i) => (
                        <span key={i} title={n} className="w-5 h-5 rounded-full bg-[#FC82ED]/20 border border-[#FC82ED]/40 text-[9px] font-bold text-[#B8369F] flex items-center justify-center">{ini(n)}</span>
                      ))}
                      <span className="text-[10px] text-[var(--muted)] ml-1">Condiviso</span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2.5">
                    {(e.immobile_titolo || e.luogo) && (
                      <button onClick={() => maps(e)} data-testid={`mob-maps-${e.id}`} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-700">
                        <MapPin size={13} /> <span className="truncate max-w-[140px]">{e.immobile_titolo || e.luogo}</span>
                      </button>
                    )}
                    {(e.immobile_titolo || e.luogo) && (
                      <button onClick={() => waze(e)} data-testid={`mob-waze-${e.id}`} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700">
                        <Navigation size={13} /> Waze
                      </button>
                    )}
                    {e.cliente_id && (
                      <button onClick={() => call(e)} data-testid={`mob-call-${e.id}`} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
                        <Phone size={13} /> <span className="truncate max-w-[140px]">{e.cliente_nome || "Chiama"}</span>
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {apptNeedsEsito(e) ? (
                      <button onClick={() => setEsitoAppt(e)} data-testid={`mob-esito-${e.id}`}
                              className="flex items-center justify-center gap-1 text-xs font-bold py-2 rounded-full bg-[#FC82ED] text-[#2B2B2B]"><Check size={14} /> Com'è andata?</button>
                    ) : e.esito_registrato ? (
                      <div data-testid={`mob-esito-done-${e.id}`} className="flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700"><Check size={14} /> Esito registrato</div>
                    ) : (
                      <div className="flex items-center justify-center text-xs text-[var(--muted)]">In programma</div>
                    )}
                    <button onClick={() => onEdit(e)} data-testid={`mob-move-${e.id}`}
                            className="flex items-center justify-center gap-1 text-xs font-bold py-2 rounded-full bg-slate-100"><CalendarClock size={14} /> Sposta</button>
                  </div>
                </div>
            ))}
          </div>
        </div>
      ))}
      </>}
      <Drawer open={drawer} onClose={() => setDrawer(false)} cur={gcur} onPick={(d) => { setGcur(startOfDay(d)); setTab("giorno"); }} onNew={onNew} onBlocca={onBlocca}
              kinds={kinds || []} selKinds={selKinds || []} toggleKind={toggleKind} />
      {esitoAppt && <EsitoModal appt={esitoAppt} onClose={() => setEsitoAppt(null)} onSaved={() => { setEsitoAppt(null); load(); if (tab === "giorno") loadGrid(); }} />}
    </div>
  );
}
